import React, { useEffect, useState } from "react";
import "./App.css";
import LoginPage from "./Pages/Login/LoginPage";
import Simulation from "./Pages/Simulation/Simulation";
import AccountSettings from "./Pages/AccountSettings/AccountSettings";
import ExamCreator from "./Pages/ExamCreator/ExamCreator";
import { AnimatePresence } from "framer-motion";
import Grading from "./Pages/Grading/Grading";
import UnauthenticatedPopup from "./Components/UnauthenticatedPopup/UnauthenticatedPopup";
import THEMES from "./Util/dataModels/Themes";
import { AppContext } from "./Util/contexts/AppContext";
import ReceiptPage from "./Pages/ReceiptPage";
import useMousePosition from "./Util/hooks/useMousePosition";
import CompletionHistoryPopup from "./Components/CompletionHistoryPopup";

const webserverRoute = "https://app.occenglish.com";

const App = () => {
  const [pageDisplayed, setPageDisplayed] = useState("Login");
  const [previousPage, setPreviousPage] = useState(null);
  const [userData, setUserData] = useState(null);
  const [printReceiptHTML, setPrintReceiptHTML] = useState(null);
  const [premadeExams, setPremadeExams] = useState([]);
  const [examSections, setExamSections] = useState([]);
  const [customExams, setCustomExams] = useState([]);
  const [firstExamSection, setFirstExamSection] = useState(null);
  const [selectedExamSections, setSelectedExamSections] = useState([]);
  const [examGuid, setExamGuid] = useState(null);
  const [primaryColor, setPrimaryColor] = useState("#0B335D");
  const [selectedThemeIndex, setSelectedThemeIndex] = useState(0);
  const [reviewLaterExams, setReviewLaterExams] = useState({});
  const [reviewLaterSections, setReviewLaterSections] = useState({});
  const [isAuthenticationErrorDisplayed, setIsAuthenticationErrorDisplayed] =
    useState(false);
  const [refreshCaseListOnNextLoad, setRefreshCaseListOnNextLoad] =
    useState(false);
  const [accentSelected, setAccentSelected] = useState("Random");
  const mousePosition = useMousePosition();
  const [viewPreviousCompletionsPopup, setViewPreviousCompletionsPopup] =
    useState(false);
  const [previousCompletionsData, setPreviousCompletionsData] = useState(null);
  const [
    previousCompletionsPopupConfiguration,
    setPreviousCompletionsPopupConfiguration,
  ] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [initialAnswers, setInitialAnswers] = useState({});

  // Navigation warning effect
  useEffect(() => {
    // Check if device is mobile
    const isMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isMobileUserAgent =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 768;

      return isTouchDevice && (isMobileUserAgent || isSmallScreen);
    };

    // Only show warnings when user is logged in (not on login page) AND on mobile
    const shouldShowWarning =
      userData && pageDisplayed !== "Login" && isMobile();

    if (!shouldShowWarning) return;

    // Warning message
    const warningMessage =
      "Are you sure you want to leave? You may lose unsaved progress.";

    // Handle page refresh, close, or navigation away from the site
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = warningMessage; // For Chrome
      return warningMessage; // For other browsers
    };

    // Handle browser back/forward navigation
    const handlePopState = (e) => {
      const confirmNavigation = window.confirm(
        "Are you sure you want to go back? You may lose unsaved progress."
      );

      if (!confirmNavigation) {
        // Push the current state back to prevent navigation
        window.history.pushState(null, null, window.location.pathname);
      }
    };

    // Add a history entry to catch back button
    window.history.pushState(null, null, window.location.pathname);

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [userData, pageDisplayed]); // Re-run when user login state or page changes

  const handleLogin = (loginData) => {
    console.log(loginData);
    setUserData(loginData);
    setPageDisplayed("ExamCreator");
  };

  const viewCaseGrades = (shouldGradingRefreshCaseList = false) => {
    setRefreshCaseListOnNextLoad(shouldGradingRefreshCaseList);
    setPageDisplayed("Grading");
  };

  const showCaseGradeAfterCompletion = () => {
    setRefreshCaseListOnNextLoad(true);
    setPreviousPage("Simulation");
    setPageDisplayed("Grading");
  };

  const handleViewGradeFromCompletionHistory = (guid) => {
    setViewPreviousCompletionsPopup(false);
    setExamGuid(guid);
    setPageDisplayed("Grading");
  };

  const updateCaseName = (newName, selectedExamId) => {
    let selectedCaseIndex = customExams.findIndex(
      (exam) => exam.examId === selectedExamId
    );
    const customExamsClone = structuredClone(customExams);
    customExamsClone[selectedCaseIndex].ExamName = newName;
    setCustomExams(customExamsClone);
  };

  const displayAuthenticationError = () => {
    setIsAuthenticationErrorDisplayed(true);
  };

  const closeGrading = () => {
    setPageDisplayed("ExamCreator");
  };

  const exitExam = async () => {
    setPageDisplayed("ExamCreator");
    // fetchCaseList();
  };

  const initializeAudioData = () => {};

  const fetchMarkReviewLater = async (examId, sectionId, newValue) => {
    return await fetch(`${webserverRoute}/reviewlater.webapi`, {
      method: "POST",
      headers: {
        Token: userData.Token,
        "Content-Type": "text/plain",
      },
      body: JSON.stringify({
        examId: examId,
        sectionId: sectionId,
        marked: newValue,
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        } else if (response.status === 401) {
          return displayAuthenticationError();
        } else {
          throw new Error(await response.text());
        }
      })
      .then((result) => {
        console.log(result);
        if (examId) {
          setReviewLaterExams({
            ...reviewLaterExams,
            [examId]: newValue,
          });
        } else {
          setReviewLaterSections({
            ...reviewLaterSections,
            [sectionId]: newValue,
          });
        }
        return true;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  };

  const handleViewPreviousSectionCompletions = ({ sectionId }) => {
    setViewPreviousCompletionsPopup(true);
    setPreviousCompletionsPopupConfiguration({
      title: `Section ${sectionId}`,
      sectionId: sectionId,
      type: "Sections",
    });
  };

  const handleViewPreviousExamCompletions = (examId) => {
    setViewPreviousCompletionsPopup(true);
    setPreviousCompletionsPopupConfiguration({
      title: `Exam ${examId}`,
      examId,
      type: "Exams",
    });
  };

  if (printReceiptHTML !== null) {
    return (
      <ReceiptPage
        html={printReceiptHTML}
        closePage={() => setPrintReceiptHTML(null)}
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      <div className="App" id="appContainer">
        <AppContext.Provider
          value={{
            userData,
            setUserData,
            route: webserverRoute,
            primaryColor,
            setPrimaryColor,
            pageDisplayed,
            setPageDisplayed,
            displayAuthenticationError,
            premadeExams,
            setPremadeExams,
            examSections,
            setExamSections,
            customExams,
            setCustomExams,
            selectedTheme: THEMES[selectedThemeIndex],
            setSelectedThemeIndex,
            selectedThemeIndex,
            setPrintReceiptHTML,
            viewCaseGrades,
            setFirstExamSection,
            setSelectedExamSections,
            setExamGuid,
            mousePosition,
            fetchMarkReviewLater,
            setRefreshCaseListOnNextLoad,
            refreshCaseListOnNextLoad,
            setReviewLaterExams,
            reviewLaterExams,
            setReviewLaterSections,
            reviewLaterSections,
            handleViewPreviousSectionCompletions,
            handleViewPreviousExamCompletions,
            showCaseGradeAfterCompletion,
            chatHistory,
            setChatHistory,
            initialAnswers,
            setInitialAnswers,
          }}
        >
          {pageDisplayed === "Login" ? (
            <LoginPage handleLogin={handleLogin} />
          ) : pageDisplayed === "Simulation" ? (
            <Simulation
              firstExamSection={firstExamSection}
              selectedExamSections={selectedExamSections}
              initializeAudioData={initializeAudioData}
              examGuid={examGuid}
              exitExam={exitExam}
              key="Simulation"
              selectedAccent={accentSelected}
              initialAnswers={initialAnswers}
            />
          ) : pageDisplayed === "ExamCreator" ? (
            <ExamCreator
              selectedSections={[]}
              updateCaseName={updateCaseName}
              accentSelected={accentSelected}
              setPreviousPage={setPreviousPage}
              handleSelectAccent={(e) => setAccentSelected(e.target.value)}
              setPreviousCompletionsData={setPreviousCompletionsData}
            />
          ) : pageDisplayed === "Grading" ? (
            <Grading
              closeGrading={closeGrading}
              selectedExamGuid={examGuid}
              previousPage={previousPage}
            />
          ) : (
            <AccountSettings />
          )}
          <AnimatePresence mode="wait">
            {isAuthenticationErrorDisplayed && (
              <UnauthenticatedPopup
                key={"UnauthenticatedPopup"}
                animationsDisabled={userData.AnimationsDisabled}
              />
            )}
            {viewPreviousCompletionsPopup && (
              <CompletionHistoryPopup
                key={`CompletionHistoryPopup - ${previousCompletionsPopupConfiguration.type}`}
                closePopup={() => setViewPreviousCompletionsPopup(false)}
                previousCompletionsData={previousCompletionsData}
                previousCompletionsPopupConfiguration={
                  previousCompletionsPopupConfiguration
                }
                setPreviousCompletionsPopupConfiguration={
                  setPreviousCompletionsPopupConfiguration
                }
                handleViewGrades={handleViewGradeFromCompletionHistory}
              />
            )}
          </AnimatePresence>
        </AppContext.Provider>
      </div>
    </AnimatePresence>
  );
};

export default App;
